<template>
    <div class="idp-dashboard">
        <!-- Dashboard Header -->
        <div class="dashboard-header">
            <div class="header-content">
                <div class="header-icon">
                    <i class="bi bi-graph-up-arrow"></i>
                </div>
                <div class="header-text">
                    <h2>Individual Development Plan</h2>
                    <p class="header-subtitle">
                        Track your progress and development across all skill areas
                    </p>
                </div>
            </div>
            <div class="header-actions">
                <div class="date-range-filter">
                    <b-form-select v-model="dateRange" size="sm" class="date-range-select" @change="onDateRangeChange">
                        <b-form-select-option value="current">Current Season</b-form-select-option>
                        <b-form-select-option value="all">All Time</b-form-select-option>
                    </b-form-select>
                </div>
                <b-button variant="success" @click="$emit('create-report')">
                    <i class="bi bi-plus-lg"></i>
                    Add IDP
                </b-button>
                <b-button v-if="currentReport" variant="outline-light" @click="$emit('edit-report', currentReport)">
                    <i class="bi bi-pencil"></i>
                    Edit Report
                </b-button>
                <b-button v-if="currentReport" variant="outline-light" title="Download this IDP as a PDF"
                    @click="$emit('export-pdf', { report: currentReport, goals, targets: gameTargets, quarter: currentQuarter })">
                    <i class="bi bi-file-earmark-pdf"></i>
                    Download PDF
                </b-button>
            </div>
        </div>

        <!-- Progress Timeline -->
        <IDPProgressTimeline v-if="reports.length > 0" :reports="reports" :selected-report="currentReport"
            @select="handleReportSelect" />

        <!-- Empty State -->
        <div v-if="!currentReport" class="empty-state">
            <div class="empty-icon">
                <i class="bi bi-clipboard2-data"></i>
            </div>
            <h3>No Development Reports Yet</h3>
            <p>Start tracking your progress by creating your first IDP report.</p>
            <b-button v-if="canEdit" variant="success" size="lg" @click="$emit('create-report')">
                <i class="bi bi-plus-lg"></i>
                Create First Report
            </b-button>
        </div>

        <!-- Current Report View -->
        <template v-if="currentReport">
            <!-- Report Card with 4 Pillars -->
            <IDPReportCard :report="currentReport" :previous-report="previousReport" :can-edit="canEdit"
                @edit="$emit('edit-report', currentReport)" />

            <!-- Comparison Mode Toggle -->
            <div v-if="previousReport" class="comparison-toggle">
                <label class="toggle-label">
                    <input type="checkbox" v-model="showComparison" />
                    <span class="toggle-switch"></span>
                    <span class="toggle-text">Show comparison with previous report</span>
                </label>
            </div>

            <!-- Comparison View -->
            <div v-if="showComparison && previousReport" class="comparison-view">
                <div class="comparison-header">
                    <h3><i class="bi bi-arrow-left-right"></i> Progress Comparison</h3>
                    <span class="comparison-dates">
                        {{ formatDate(previousReport.date) }} → {{ formatDate(currentReport.date) }}
                    </span>
                </div>
                <div class="comparison-grid">
                    <div v-for="pillar in pillars" :key="pillar.key" class="comparison-item">
                        <span class="pillar-name">{{ pillar.label }}</span>
                        <div class="comparison-bars">
                            <div class="bar-row">
                                <span class="bar-label">Before</span>
                                <div class="bar-container">
                                    <div class="bar green"
                                        :style="{ width: getPercentage(previousReport[pillar.green]) + '%' }"></div>
                                    <div class="bar yellow"
                                        :style="{ width: getPercentage(previousReport[pillar.yellow]) + '%' }"></div>
                                    <div class="bar red"
                                        :style="{ width: getPercentage(previousReport[pillar.red]) + '%' }"></div>
                                </div>
                            </div>
                            <div class="bar-row">
                                <span class="bar-label">After</span>
                                <div class="bar-container">
                                    <div class="bar green"
                                        :style="{ width: getPercentage(currentReport[pillar.green]) + '%' }"></div>
                                    <div class="bar yellow"
                                        :style="{ width: getPercentage(currentReport[pillar.yellow]) + '%' }"></div>
                                    <div class="bar red"
                                        :style="{ width: getPercentage(currentReport[pillar.red]) + '%' }"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Game Targets Section -->
            <IDPGameTargets :targets="gameTargets" :initial-quarter="currentQuarter" :can-edit="canEdit"
                @add="$emit('add-target')" @edit="target => $emit('edit-target', target)"
                @quarter-change="q => $emit('quarter-change', q)" />

            <!-- Goals Section -->
            <IDPGoals :goals="goals" :can-edit="canEdit" @add="$emit('add-goal')"
                @edit="goal => $emit('edit-goal', goal)" />
        </template>
    </div>
</template>

<script>
import IDPProgressTimeline from './IDPProgressTimeline.vue';
import IDPReportCard from './IDPReportCard.vue';
import IDPGameTargets from './IDPGameTargets.vue';
import IDPGoals from './IDPGoals.vue';
import moment from 'moment';

export default {
    name: 'IDPDashboard',
    components: {
        IDPProgressTimeline,
        IDPReportCard,
        IDPGameTargets,
        IDPGoals
    },
    props: {
        reports: {
            type: Array,
            default: () => []
        },
        gameTargets: {
            type: Array,
            default: () => []
        },
        goals: {
            type: Array,
            default: () => []
        },
        currentQuarter: {
            type: [String, Number],
            default: ''
        },
        canEdit: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            selectedReportId: null,
            showComparison: false,
            dateRange: 'current',
            pillars: [
                { key: 'technical', label: 'Technical', green: 'te_ex', yellow: 'te_co', red: 'te_de' },
                { key: 'tactical', label: 'Tactical', green: 'ta_ex', yellow: 'ta_co', red: 'ta_de' },
                { key: 'physical', label: 'Physical', green: 'ph_ex', yellow: 'ph_co', red: 'ph_de' },
                { key: 'mental', label: 'Mental', green: 'me_ex', yellow: 'me_co', red: 'me_de' }
            ]
        };
    },
    computed: {
        sortedReports() {
            return [...this.reports].sort((a, b) =>
                new Date(b.created_date) - new Date(a.created_date)
            );
        },
        currentReport() {
            if (this.selectedReportId) {
                return this.reports.find(r => r.id === this.selectedReportId);
            }
            return this.sortedReports[0] || null;
        },
        previousReport() {
            if (!this.currentReport) return null;
            const currentIndex = this.sortedReports.findIndex(
                r => r.id === this.currentReport.id
            );
            return this.sortedReports[currentIndex + 1] || null;
        }
    },
    watch: {
        reports: {
            immediate: true,
            handler(newReports) {
                if (newReports.length && !this.selectedReportId) {
                    const sorted = [...newReports].sort((a, b) =>
                        new Date(b.created_date) - new Date(a.created_date)
                    );
                    this.selectedReportId = sorted[0]?.id;
                }
            }
        }
    },
    methods: {
        onDateRangeChange() {
            this.selectedReportId = null;
            this.$emit('date-range-change', this.dateRange);
        },
        handleReportSelect(report) {
            this.selectedReportId = report.id;
            this.showComparison = false;
            this.$emit('report-selected', report);
        },
        formatDate(date) {
            return moment(date).format('DD MMM YYYY');
        },
        getPercentage(value) {
            const total = 100;
            return Math.min(100, Math.max(0, (value / total) * 100));
        }
    }
};
</script>

<style scoped lang="scss">
.idp-dashboard {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

/* Header */
.dashboard-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 1rem;
    background: #88a388;
    border-radius: 12px;
    padding: 1.5rem;
    color: #fff;
}

.header-content {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
}

.header-icon {
    width: 56px;
    height: 56px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;

    i {
        font-size: 1.75rem;
    }
}

.header-text {
    h2 {
        margin: 0 0 0.375rem;
        font-size: 1.5rem;
        font-weight: 700;
    }

    .header-subtitle {
        margin: 0;
        font-size: 0.9375rem;
        opacity: 0.9;
    }
}

.header-actions {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    flex-wrap: wrap;

    .btn {
        display: flex;
        align-items: center;
        gap: 0.375rem;
    }
}

.date-range-filter {
    .date-range-select {
        background: #fff;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        color: #374151;
        font-weight: 500;
        min-width: 160px;
        padding: 0.5rem 2.25rem 0.5rem 0.75rem;
        font-size: 0.875rem;
        cursor: pointer;
        appearance: none;
        background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e");
        background-position: right 0.5rem center;
        background-repeat: no-repeat;
        background-size: 1.5em 1.5em;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        transition: all 0.15s ease;

        &:hover {
            border-color: #9ca3af;
            background-color: #f9fafb;
        }

        &:focus {
            outline: none;
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
        }
    }
}

/* Empty State */
.empty-state {
    text-align: center;
    padding: 4rem 2rem;
    background: #fff;
    border: 2px dashed #cbd5e1;
    border-radius: 12px;
}

.empty-icon {
    width: 80px;
    height: 80px;
    margin: 0 auto 1.5rem;
    background: #f1f5f9;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    i {
        font-size: 2.5rem;
        color: #94a3b8;
    }
}

.empty-state h3 {
    margin: 0 0 0.5rem;
    font-size: 1.25rem;
    color: #334155;
}

.empty-state p {
    margin: 0 0 1.5rem;
    color: #64748b;
}

/* Comparison Toggle */
.comparison-toggle {
    display: flex;
    justify-content: center;
    margin: -0.5rem 0;
}

.toggle-label {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    cursor: pointer;
    padding: 0.75rem 1.25rem;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 25px;
    transition: all 0.2s;

    &:hover {
        background: #f1f5f9;
    }

    input {
        display: none;
    }
}

.toggle-switch {
    position: relative;
    width: 44px;
    height: 24px;
    background: #cbd5e1;
    border-radius: 12px;
    transition: background 0.2s;

    &::after {
        content: '';
        position: absolute;
        top: 2px;
        left: 2px;
        width: 20px;
        height: 20px;
        background: #fff;
        border-radius: 50%;
        transition: transform 0.2s;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    }

    input:checked + & {
        background: #3b82f6;

        &::after {
            transform: translateX(20px);
        }
    }
}

.toggle-text {
    font-size: 0.875rem;
    color: #475569;
    font-weight: 500;
}

/* Comparison View */
.comparison-view {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 1.5rem;
}

.comparison-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-bottom: 1.5rem;

    h3 {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin: 0;
        font-size: 1.125rem;
        color: #1e40af;

        i {
            font-size: 1.25rem;
        }
    }

    .comparison-dates {
        font-size: 0.875rem;
        color: #64748b;
        background: #f1f5f9;
        padding: 0.375rem 0.75rem;
        border-radius: 6px;
    }
}

.comparison-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.25rem;
}

.comparison-item {
    background: #f8fafc;
    border-radius: 8px;
    padding: 1rem;
}

.pillar-name {
    display: block;
    font-weight: 600;
    color: #334155;
    margin-bottom: 0.75rem;
}

.comparison-bars {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.bar-row {
    display: flex;
    align-items: center;
    gap: 0.625rem;
}

.bar-label {
    font-size: 0.75rem;
    color: #64748b;
    width: 40px;
    flex-shrink: 0;
}

.bar-container {
    flex: 1;
    height: 16px;
    background: #e2e8f0;
    border-radius: 8px;
    display: flex;
    overflow: hidden;
}

.bar {
    height: 100%;
    transition: width 0.3s ease;

    &.green {
        background: #22c55e;
    }

    &.yellow {
        background: #f59e0b;
    }

    &.red {
        background: #ef4444;
    }
}

/* Mobile */
@media (max-width: 768px) {
    .dashboard-header {
        flex-direction: column;
        padding: 1.25rem;
    }

    .header-content {
        flex-direction: column;
        text-align: center;
        align-items: center;
    }

    .header-icon {
        width: 48px;
        height: 48px;

        i {
            font-size: 1.5rem;
        }
    }

    .header-text h2 {
        font-size: 1.25rem;
    }

    .header-actions {
        width: 100%;
        flex-direction: column;

        .btn {
            justify-content: center;
        }

        .date-range-filter {
            width: 100%;

            .date-range-select {
                width: 100%;
            }
        }
    }

    .empty-state {
        padding: 2.5rem 1.5rem;
    }

    .empty-icon {
        width: 64px;
        height: 64px;

        i {
            font-size: 2rem;
        }
    }

    .comparison-grid {
        grid-template-columns: 1fr;
    }

    .toggle-label {
        padding: 0.625rem 1rem;
    }

    .toggle-text {
        font-size: 0.8125rem;
    }
}

@media (max-width: 480px) {
    .idp-dashboard {
        gap: 1rem;
    }

    .dashboard-header {
        padding: 1rem;
    }

    .header-icon {
        width: 40px;
        height: 40px;

        i {
            font-size: 1.25rem;
        }
    }

    .header-text h2 {
        font-size: 1.125rem;
    }

    .header-text .header-subtitle {
        font-size: 0.8125rem;
    }

    .comparison-view {
        padding: 1rem;
    }

    .comparison-header h3 {
        font-size: 1rem;
    }

    .comparison-item {
        padding: 0.75rem;
    }

    .bar-container {
        height: 12px;
    }
}
</style>
